import React from "react";

import { periodRangeLabel } from "../../../pages/admin/revenueAnalytics/revenueAnalyticsFormat";
import { usePerformanceData } from "./usePerformanceData";
import { selectDataQualityNotices } from "./performanceSelectors";
import PerformanceKpiGrid from "./PerformanceKpiGrid";
import PerformanceInsightBanner from "./PerformanceInsightBanner";
import PerformanceRankingCard from "./PerformanceRankingCard";
import PerformanceWeakestUnitCard from "./PerformanceWeakestUnitCard";
import PerformanceUnitsCard from "./PerformanceUnitsCard";
import PerformanceOccupancyCard from "./PerformanceOccupancyCard";
import PerformanceSecondaryKpis from "./PerformanceSecondaryKpis";
import PerformanceUnsupportedPeriod from "./PerformanceUnsupportedPeriod";

/**
 * The whole Performance panel (P4). One request, one month, seven sections.
 *
 * OWNERSHIP
 * ---------
 * S4 moved three surfaces in here and left the rest inline on
 * `AdminUebersichtPage`, because that slice was a contract migration and moving
 * a surface whose numbers do not change would have hidden the ones that did. P4
 * finishes the move: the page now renders this component and nothing else in the
 * Performance panel, and hands down the legacy KPI payload it already holds. No
 * request is issued from here beyond the one `usePerformanceData` owns — the
 * secondary figures arrive as props from state the page fetched anyway.
 *
 * WHAT THIS TAB DOES NOT DO, ON PURPOSE
 * -------------------------------------
 * No month-over-month delta. No sparkline. No six-month history. `previous` is
 * on the wire and is rendered as a labelled second reading; subtracting it would
 * define growth in a browser beside figures the backend defines exactly, and two
 * points drawn as a line is a trend claim the data cannot support. The old
 * six-month room chart is gone rather than restyled: it counted rooms on a
 * month's last day, while everything here is room-days across the whole month,
 * and putting both on one screen would have left a reader to pick.
 *
 * THE PERIOD (P2)
 * ---------------
 * The picker can emit a range, and this tab now ANSWERS for it. It used to take
 * the range's to-month and say so with "· Stand <Monat>" — which is why a filter
 * reading "April – September 2026" could show September's figures. Every dated
 * surface now names the period the SERVER answered for. Nothing is aggregated
 * here: a period is ONE request, and the fold lives in the service beside the
 * monthly truth. "Gesamte Zeit" stays unanswerable and is refused explicitly.
 *
 * LAYOUT
 * ------
 * KPI row, then the reading aid, then ranking beside the weakest unit, then the
 * two full-width lists, then the legacy block last and visibly apart. Ranking and
 * weakest unit share a row on a wide screen because they answer the same question
 * from both ends; everything else stacks.
 */
export default function PerformanceTab({
  period,
  active = true,
  legacyKpis = null,
  legacyKpisLoading = false,
  portfolioPotential = 0,
  portfolioLoading = false,
}) {
  const { data, loading, error, reload, unsupported } = usePerformanceData(period, {
    enabled: active,
  });

  // A closed tab renders nothing. The page mounts all five panels at once, so
  // without this the cards would already be standing there — and since the hook
  // reads nothing while closed, they would be standing there as em-dashes, which
  // says "we asked and got nothing" about data nobody has asked for yet. (The
  // hook is still called above: hooks run unconditionally, and its own `enabled`
  // gate is what keeps the request from firing.)
  if (!active) return null;

  // "Gesamte Zeit" has no answerable Performance window: the contract bounds a
  // period at 24 months, and an unbounded operating margin has no denominator.
  // Said plainly, rather than answered with the to-month — which is what this tab
  // used to do, and is the whole reason a filter reading "April – September"
  // could show September's figures.
  if (unsupported) return <PerformanceUnsupportedPeriod />;

  // THE PERIOD ON SCREEN IS THE ONE THE SERVER ANSWERED FOR, not the one the
  // picker asked for. They agree in practice, but only the response knows which
  // window was actually computed — and `period.year`/`.month` are deliberately
  // NOT used here: they carry the TO-month for backward compatibility, so
  // reading them would put "September 2026" over a six-month figure, which is
  // precisely the bug this slice removes.
  const ref = data?.period;
  const isRange = Boolean(ref?.is_range);
  const periodText = ref ? periodRangeLabel(ref.start, ref.end) : "";
  const stand = periodText ? ` · ${periodText}` : "";
  const comparisonText = ref
    ? periodRangeLabel(ref.comparison_start, ref.comparison_end)
    : "";

  // ONE error state for the whole tab: every figure comes from one request, so a
  // message per card would report a single failure seven times.
  if (error) {
    return (
      <section data-testid="company-performance-tab" className="space-y-2">
        <p
          role="alert"
          className="rounded-xl border border-border bg-card px-3 py-2 text-[12px] text-destructive"
        >
          {error}
        </p>
        <button
          type="button"
          onClick={reload}
          className="rounded-md border border-border bg-transparent px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Erneut versuchen
        </button>
      </section>
    );
  }

  const notices = selectDataQualityNotices(data?.data_quality);

  return (
    <section data-testid="company-performance-tab" aria-busy={loading || undefined}>
      <header className="mb-4">
        <h1 className="m-0 text-[15px] font-semibold text-foreground">Performance</h1>
        {/* "Trends" is deliberately absent from this sentence. The tab answers
            for one month and states the one before it; calling that a trend
            would promise a history the contract does not carry. */}
        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
          {`Umsatz, Objektergebnis und Auslastung auf einen Blick${stand}`}
        </p>
      </header>

      <div className="space-y-4">
        <PerformanceKpiGrid
          selected={data?.selected}
          previous={data?.previous}
          methodology={data?.methodology}
          isRange={isRange}
          comparisonText={comparisonText}
          loading={loading}
        />

        <PerformanceInsightBanner methodology={data?.methodology} />

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <PerformanceRankingCard
              ranking={data?.selected_ranking}
              stand={stand}
              isRange={isRange}
              loading={loading}
            />
          </div>
          <PerformanceWeakestUnitCard
            ranking={data?.selected_ranking}
            stand={stand}
            loading={loading}
          />
        </div>

        <PerformanceOccupancyCard
          selected={data?.selected}
          previous={data?.previous}
          stand={stand}
          isRange={isRange}
          loading={loading}
        />

        <PerformanceUnitsCard
          period={data?.selected}
          stand={stand}
          isRange={isRange}
          loading={loading}
        />

        {notices.length ? (
          <section
            data-testid="performance-data-quality"
            aria-labelledby="performance-data-quality-heading"
            className="rounded-xl border border-border bg-muted/40 p-4"
          >
            <h2
              id="performance-data-quality-heading"
              className="m-0 text-[12px] font-semibold text-foreground"
            >
              Hinweise zur Datenlage
            </h2>
            {/* Neutral disclosure, no severity: the contract publishes counts
                and deliberately no threshold, so calling one "kritisch" here
                would be a judgement no service stands behind. Nothing renders
                at all when both signals are zero. */}
            <ul className="mt-1.5 list-none space-y-1 p-0">
              {notices.map((notice) => (
                <li
                  key={notice.key}
                  className="text-[11px] leading-[1.5] text-muted-foreground"
                >
                  {notice.text}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="border-t border-border pt-4">
          <PerformanceSecondaryKpis
            legacyKpis={legacyKpis}
            legacyKpisLoading={legacyKpisLoading}
            portfolioPotential={portfolioPotential}
            portfolioLoading={portfolioLoading}
            selected={data?.selected}
            isRange={isRange}
            loading={loading}
          />
        </div>
      </div>
    </section>
  );
}
